/**
 * The catalog page's data lifetime (WID-12).
 *
 * The published snapshot is read over HTTP and then kept current over
 * realtime. Those are separate failures: a browser or network that allows the
 * HTTP read but blocks the realtime connection still has a usable catalog, so
 * a subscription that cannot be set up leaves the snapshot on screen (marked
 * not live) and is retried with a capped backoff. Once it connects, the
 * snapshot is read again so changes made in the meantime are not missed.
 */

export type LiveCatalogState<Snapshot> =
  | { status: "loading" }
  | { status: "ready"; snapshot: Snapshot; live: boolean }
  | { status: "missing" }
  | { status: "error" };

export const RETRY_BASE_MS = 2_000;
export const RETRY_MAX_MS = 60_000;

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: number }).code === 404
  );
}

export function startLiveCatalog<Snapshot>(deps: {
  load: () => Promise<Snapshot>;
  subscribe: (
    onSnapshot: (snapshot: Snapshot) => void,
    onDelete: () => void,
  ) => Promise<() => void>;
  onState: (state: LiveCatalogState<Snapshot>) => void;
  /** Runs `fn` after `ms`; returns a cancel function. */
  schedule?: (fn: () => void, ms: number) => () => void;
}): () => void {
  const schedule =
    deps.schedule ??
    ((fn: () => void, ms: number) => {
      const handle = setTimeout(fn, ms);
      return () => clearTimeout(handle);
    });
  let active = true;
  let unsubscribe: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  let snapshot: Snapshot | null = null;
  let failures = 0;

  const show = (next: Snapshot, live: boolean) => {
    snapshot = next;
    deps.onState({ status: "ready", snapshot: next, live });
  };

  const connect = () => {
    cancelRetry = null;
    if (!active) return;
    void deps
      .subscribe(
        (next) => {
          if (active) show(next, true);
        },
        () => {
          if (active) deps.onState({ status: "missing" });
        },
      )
      .then((cleanup) => {
        if (!active) {
          cleanup();
          return;
        }
        unsubscribe = cleanup;
        const reconnected = failures > 0;
        failures = 0;
        if (snapshot) show(snapshot, true);
        if (!reconnected) return;
        // Changes published while live updates were down.
        void deps
          .load()
          .then((next) => {
            if (active) show(next, true);
          })
          .catch((error: unknown) => {
            if (active && isNotFound(error)) deps.onState({ status: "missing" });
          });
      })
      .catch(() => {
        if (!active) return;
        failures += 1;
        if (snapshot) show(snapshot, false);
        const delay = Math.min(
          RETRY_MAX_MS,
          RETRY_BASE_MS * 2 ** Math.min(failures - 1, 10),
        );
        cancelRetry = schedule(connect, delay);
      });
  };

  deps.onState({ status: "loading" });
  void deps
    .load()
    .then((first) => {
      if (!active) return;
      show(first, false);
      connect();
    })
    .catch((error: unknown) => {
      if (!active) return;
      deps.onState({ status: isNotFound(error) ? "missing" : "error" });
    });

  return () => {
    active = false;
    cancelRetry?.();
    cancelRetry = null;
    unsubscribe?.();
    unsubscribe = null;
  };
}
